import type { DataTransferObject } from '@svenskaspelab/nova-payment-sport';
import { createLogger } from '@svs/nova-core/log';
import type { trackEcommerce, trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { Role } from '@svs/nova-core/nextjs/auth';
import type { useDialog } from '@svs/nova-foundation/nextjs/dialog';

import { type Coupon, getCouponId, getCouponIndex } from '../../../../schema/coupon/entry.js';
import type { Commission } from '../../../../schema/input/entry.js';
import type { ActivatedDrawId } from '../../../../schema/marketplace/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import { CouponEntity, NotInitializedError, PoolbettingError } from '../../../domain/entry.js';
import type { ActionResult } from '../../../lib/entry.browser.js';

const logger = createLogger('poolbetting.place-coupon-action');

type PlaceExternalCouponArgs = {
  coupon: Coupon;
  showDialog: ReturnType<typeof useDialog>['showDialog'];
};

type PlaceExternalCouponReturn = ActionResult<{
  transactionIds: DataTransferObject['transactionId'][];
  paidActivatedDrawIds: ActivatedDrawId[];
}>;

type PaymentContextArgs = {
  role: Role;
  commissionPercent?: Commission;
  activatedDrawId?: ActivatedDrawId;
  paymentUrls?: Record<string, string | undefined>;
  marketplaceWallets?: Record<ActivatedDrawId, { balance: number; soldShares: number; sharePrice: number }>;
  trackTransaction: typeof trackEcommerce;
  trackEventFunction: typeof trackEvent;
};

/**
 * Places a valid external coupon.
 *
 * @param props Place coupon args
 * @param props.coupon Coupon to place bet on
 * @param props.showDialog Dialog function to show confirmation dialog
 * @param paymentContext Payment context args such as user role and optional commission percentage
 * @returns status: SUCCESS with data if successful, otherwise status: ERROR with error message
 */
export async function placeExternalCouponWager(
  { coupon, showDialog }: PlaceExternalCouponArgs,
  paymentContext: PaymentContextArgs
): Promise<PlaceExternalCouponReturn> {
  logger.info('[placeExternalCouponWager] action invoked', {
    product: coupon.product,
    couponType: coupon.couponType,
  });

  const createExternalWagerController = getInstanceClient('ICreateExternalWagerController');
  const definitionService = getInstanceClient('IDefinitionService');
  const getCouponWalletsController = getInstanceClient('IGetCouponWalletsController');

  const couponId = getCouponId(coupon.product, coupon.drawNumber, coupon.couponType);
  const wallets = getCouponWalletsController({ couponId });

  try {
    const definition = await definitionService.resolveDefinitionAsync(coupon.product);

    if (!definition) {
      throw new NotInitializedError(`Unable to load definition for product: ${coupon.product}`);
    }

    if (CouponEntity.getStatus(coupon, { definition: definition.model }) !== 'VALID') {
      logger.info('coupon is not VALID');
      return { status: 'ERROR', error: 'Ogiltig kupong' };
    }

    const couponIndex = 0;

    if (!getCouponIndex(couponIndex)) {
      logger.info('invalid couponIndex', { couponIndex });
      return { status: 'ERROR', error: 'Ogiltig kupong' };
    }

    const validCoupon = new CouponEntity(coupon, couponIndex, definition);

    const wagerResponse = await createExternalWagerController({
      coupon: validCoupon,
      couponIndex,
      wallets,
      showDialog,
      paymentContext,
    });

    return {
      status: 'SUCCESS',
      data: {
        transactionIds: wagerResponse.transactionIds,
        paidActivatedDrawIds: wagerResponse.paidActivatedDrawIds,
      },
    };
  } catch (error) {
    if (PoolbettingError.isInstance(error)) {
      return error.toActionResult();
    }

    logger.error('Failed to place coupon wager', { error, couponId });

    return { status: 'ERROR', error: 'Något gick fel vid betalningen' };
  }
}
